/**
 * ThreadMessage — one email in a thread, as the design draws it.
 *
 *   ┌─────────────────────────────────────────────────────────────────┐
 *   │ (MV)  Marisol Vance  marisol@vancewellness.co   2:41 PM ↩ ↪ ⋯ ⌃ │
 *   │       to James Saludario <support@aicoaches.com>                │
 *   │                                                                 │
 *   │       That works.                                               │
 *   │       …                                                         │
 *   └─────────────────────────────────────────────────────────────────┘
 *
 * ## This used to be a card, and the card was the bug
 *
 * Every message rendered as `mx-4 my-2 rounded-lg border shadow-sm` floating on
 * `bg-stone-50`, with a grey sub-header, a permanent `To:` block and a
 * Reply/Reply All/Forward strip along the bottom. Four messages produced four
 * boxes, twelve horizontal rules and three distinct greys before a single line
 * of the actual mail. The design draws a flat row separated by ONE hairline —
 * so the eye travels down the senders, and the message body is the only thing
 * with any visual weight.
 *
 * ## The whole header is the toggle
 *
 * Not a chevron button with a 16px hit area. `onClick` sits on the header row,
 * and the three action buttons inside it `stopPropagation` — which is why they
 * are buttons and not divs, and why each one's handler is wrapped rather than
 * passed straight through. Getting this backwards means every Reply click also
 * collapses the message you are replying to.
 *
 * ## What the prototype does not draw, and is kept anyway
 *
 * `Reply all` lives in the `⋯` menu. The design has no per-message Reply All at
 * all — thread-level Reply all is in the footer — but the unified reader has
 * shipped one for as long as it has existed, and deleting a working control is
 * a capability loss rather than a design refinement (the same rule
 * `surfaceContract` applies to the personal list's Reply). The menu is where it
 * costs nothing.
 *
 * Likewise the `sent as` chip, the synthetic badge and `Delivered to:` have no
 * slot in the prototype because the prototype has no distribution list, no
 * reconstructed messages and no BCC. They stay: each one is the answer to
 * "why did this go out from that address", and a reader that silently drops
 * them is not simpler, it is less true.
 *
 * Body rendering stays a sandboxed iframe. The prototype prints paragraphs
 * because its mail is a fixture; real mail is attacker-controlled HTML.
 * The GEOMETRY matches (74px indent, 78ch measure, 14px/1.65) — the isolation
 * does not move.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Archive,
    ChevronDown,
    ChevronUp,
    CircleHelp,
    CircleX,
    Code,
    CornerUpLeft,
    CornerUpRight,
    Filter,
    Tag,
    Loader2,
    Mail,
    MessageSquare,
    MoreHorizontal,
    Paperclip,
    Printer,
    ReplyAll,
    Shield,
    Trash2,
} from 'lucide-react';
import {
    formatEmailDate,
    sanitizeEmailHtml,
    useIframeAutoResize,
    SyntheticMessageBadge,
} from './inboxUtils';
import { avatarSolid, initials } from './avatars';
import { formatBytes } from './AttachmentBar';
import { splitQuotedHtml, splitQuotedText } from './quotedBody';
import useDisplayZone from '../../hooks/useDisplayZone';
import { useProxiedEmailImages } from './emailImages';
import { parseFromName } from './inboxRules';
import MailTagPicker, { MailTagChips } from './MailTagPicker';

/** A 26×26 header action. Stops the click reaching the row's toggle. */
function HeaderAction({ icon: Icon, label, onClick, disabled, testId, ...rest }) {
    const isDisabled = disabled || typeof onClick !== 'function';
    return (
        <button
            type="button"
            title={label}
            aria-label={label}
            disabled={isDisabled}
            data-testid={testId}
            onClick={
                isDisabled
                    ? undefined
                    : (event) => {
                        event.stopPropagation();
                        onClick(event);
                    }
            }
            className={`flex-shrink-0 grid place-items-center w-[26px] h-[26px] rounded-[7px]
                        ${isDisabled
                            ? 'text-[#D6D3CD] cursor-not-allowed'
                            : 'text-[#57504B] hover:bg-[#F1EFEA]'}`}
            {...rest}
        >
            <Icon className="w-[15px] h-[15px]" />
        </button>
    );
}

/**
 * The per-message `⋯` menu — the design's fifteen rows, in its four groups.
 *
 * **Items with no backend render disabled, never hidden.** Share in chat,
 * Block, Report spam, Report phishing, Filter messages like this, Download and
 * Show original have no endpoint anywhere in this repo; they are drawn because
 * the design draws them, and greyed because wiring them to a toast or a no-op
 * is how a reader comes to look finished while doing nothing. Delete and Mark
 * as unread disable themselves the same way when the parent passes no handler —
 * so "unsupported on this surface" and "not built yet" look identical to the
 * user, which is the truth in both cases.
 */
function MessageMenu({
    entry,
    displayName,
    onReply,
    onReplyAll,
    onForward,
    onDelete,
    onMarkUnread,
    onPrint,
    onEditTags,
    onClose,
}) {
    const ref = useRef(null);

    useEffect(() => {
        const onDocClick = (event) => {
            if (ref.current && !ref.current.contains(event.target)) onClose();
        };
        const onKey = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    const item = (Icon, label, testId, handler, hint) => {
        const disabled = typeof handler !== 'function';
        return (
            <button
                key={testId}
                type="button"
                role="menuitem"
                disabled={disabled}
                title={hint || undefined}
                data-testid={testId}
                onClick={
                    disabled
                        ? undefined
                        : (event) => {
                            event.stopPropagation();
                            onClose();
                            handler(entry);
                        }
                }
                className={`w-full flex items-center gap-[10px] text-left px-[10px] py-[7px]
                            rounded-[7px] text-[13px] ${
                                disabled
                                    ? 'text-[#C4BFB9] cursor-not-allowed'
                                    : 'text-[#26221F] hover:bg-[#F4F2EE]'
                            }`}
            >
                <Icon
                    className={`w-[15px] h-[15px] flex-shrink-0 ${
                        disabled ? 'text-[#C4BFB9]' : 'text-[#57504B]'
                    }`}
                />
                <span className="flex-1 truncate">{label}</span>
            </button>
        );
    };
    const rule = (key) => (
        <div key={key} className="my-[5px] h-px bg-[#EDEBE6]" aria-hidden="true" />
    );
    const unbacked = 'No backend for this yet';
    const id = entry.comm_id;

    return (
        <div
            ref={ref}
            role="menu"
            data-testid={`thread-message-menu-${id}`}
            onClick={(event) => event.stopPropagation()}
            className="absolute right-0 top-[32px] z-[34] w-[268px] p-[6px]
                       rounded-[11px] bg-white border border-[#E9E7E2]
                       shadow-[0_14px_36px_rgba(12,10,9,.16)]"
        >
            {item(CornerUpLeft, 'Reply', `thread-menu-reply-${id}`, onReply)}
            {item(ReplyAll, 'Reply all', `thread-menu-reply-all-${id}`, onReplyAll)}
            {item(CornerUpRight, 'Forward', `thread-menu-forward-${id}`, onForward)}
            {item(MessageSquare, 'Share in chat', `thread-menu-share-${id}`, null, unbacked)}
            {rule('r1')}
            {item(Trash2, 'Delete', `thread-menu-delete-${id}`, onDelete)}
            {item(Mail, 'Mark as unread', `thread-menu-mark-unread-${id}`, onMarkUnread,
                typeof onMarkUnread === 'function'
                    ? undefined
                    : 'Mark unread is not wired on this surface')}
            {/* #646 — tags THIS message (the toolbar's Tag acts on the conversation). */}
            {item(Tag, 'Tags…', `thread-menu-tags-${id}`, onEditTags,
                typeof onEditTags === 'function'
                    ? undefined
                    : 'Tagging is not available here')}
            {rule('r2')}
            {item(CircleX, `Block "${displayName}"`, `thread-menu-block-${id}`, null, unbacked)}
            {item(CircleHelp, 'Report spam', `thread-menu-report-spam-${id}`, null, unbacked)}
            {item(Shield, 'Report phishing', `thread-menu-report-phishing-${id}`, null, unbacked)}
            {rule('r3')}
            {item(Filter, 'Filter messages like this', `thread-menu-filter-${id}`, null, unbacked)}
            {item(Printer, 'Print message', `thread-menu-print-${id}`, onPrint)}
            {item(Archive, 'Download message', `thread-menu-download-${id}`, null, unbacked)}
            {item(Code, 'Show original', `thread-menu-source-${id}`, null, unbacked)}
        </div>
    );
}

/**
 * Body resolution:
 *  - Unified inbox: prefers body_html (full HTML from Graph), falls back to body.
 *  - Personal inbox: prefers body_html, falls back to body, then snippet.
 *    Personal inbox fields: sent_at, date, from_email, contact_email, snippet.
 *
 * Wrapped in React.memo to prevent iframe flicker on parent re-renders.
 */
const ThreadMessage = React.memo(function ThreadMessage({
    entry,
    inbox,
    canReplyAllowed,
    onReply,
    onReplyAll,
    onForward,
    composer,
    /**
     * Initial collapse state in UNCONTROLLED mode. A thread collapses every
     * card but the newest; the parent decides which that is, because a message
     * cannot know its own position.
     */
    defaultCollapsed = false,
    /**
     * CONTROLLED mode: pass a boolean and the parent owns the state. The
     * reader needs this — a collapsed message must not fetch its body, and the
     * fetch lives in the parent, so the parent has to know what is open.
     */
    collapsed: collapsedProp,
    onToggleCollapse,
    /** `⋯` items that exist only where a handler is wired. */
    onDelete,
    onMarkUnread,
    /** `(entry, attachment) => void`. Absent ⇒ the chips render disabled. */
    /**
     * `(entry, { add, remove }) => Promise` — tag_ids to add/remove on THIS
     * message (#646). Absent ⇒ the ⋯ menu's Tags item renders disabled.
     */
    onChangeTags,
    onDownloadAttachment,
    /**
     * `() => headers`. Passed in rather than read from `useAuth` here: this
     * is a leaf, and reaching for the auth context would put every consumer —
     * and every test that renders one message — under an `AuthProvider` for
     * the sake of a progressive enhancement. Absent, remote images simply are
     * not proxied, which is the behaviour before this existed.
     */
    getAuthHeader,
}) {
    const iframeRef = useRef(null);
    const displayZone = useDisplayZone();
    const [loaded, setLoaded] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [tagsOpen, setTagsOpen] = useState(false);
    const [internalCollapsed, setInternalCollapsed] = useState(Boolean(defaultCollapsed));
    const isControlled = typeof collapsedProp === 'boolean';
    const collapsed = isControlled ? collapsedProp : internalCollapsed;
    const toggleCollapse = () => {
        // `collapsed` is the state BEFORE the toggle, so it is exactly the
        // expanded state after it. Named, because an un-named boolean flip is
        // the kind of thing that gets "fixed" backwards later.
        const nextExpanded = collapsed;
        if (!isControlled) setInternalCollapsed((c) => !c);
        onToggleCollapse?.(entry, nextExpanded);
    };
    // #624/#659: a reply opens UNDER the message it answers, so that message
    // has to be readable. Transition-only (no composer → composer), so a user
    // who then collapses the card while the draft is open is not fought on
    // every render. Controlled (personal) mode goes through onToggleCollapse,
    // which also enables that lane's deferred body fetch.
    const composerRef = useRef(null);
    const hasComposer = Boolean(composer);
    const prevHasComposer = useRef(hasComposer);
    useEffect(() => {
        const opened = hasComposer && !prevHasComposer.current;
        prevHasComposer.current = hasComposer;
        if (!opened) return undefined;
        if (collapsed) {
            if (!isControlled) setInternalCollapsed(false);
            onToggleCollapse?.(entry, true);
        }
        // After the expand commits: bring the card into view and put the caret
        // in its editor. The optional calls keep jsdom (no layout) safe.
        const timer = setTimeout(() => {
            const node = composerRef.current;
            node?.scrollIntoView?.({ block: 'nearest' });
            node?.querySelector?.('[contenteditable="true"]')?.focus?.({ preventScroll: true });
        }, 0);
        return () => clearTimeout(timer);
    }, [hasComposer]); // eslint-disable-line react-hooks/exhaustive-deps -- transition-only by design: it must fire when a composer appears and on nothing else

    const senderName = parseFromName(entry);
    const senderAddress = entry.from_address || entry.from_email || entry.contact_email || '';
    const displayName = senderName || entry.from_address || entry.from_email
        || entry.contact_name || 'Unknown sender';

    // Body resolution — handles both unified and personal inbox field names.
    // Unified: body_html (full HTML from Graph), body (bodyPreview plain text).
    // Personal: body_html (from sequence send), body (plain text), snippet.
    const [showQuoted, setShowQuoted] = useState(false);
    const rawBody = entry.body_html || '';
    const isHtml = Boolean(entry.body_html);
    const textBody = entry.body || entry.snippet || '';
    const sanitized = useMemo(
        () => (isHtml ? sanitizeEmailHtml(rawBody) : ''),
        [isHtml, rawBody]
    );

    // Hide the quoted original by default. In a thread view the message being
    // quoted is already rendered as its own row directly above.
    const htmlSplit = useMemo(
        () => (isHtml ? splitQuotedHtml(sanitized) : { visible: '', quoted: '', marker: null }),
        [isHtml, sanitized]
    );
    const textSplit = useMemo(() => splitQuotedText(textBody), [textBody]);
    const hasQuote = isHtml ? Boolean(htmlSplit.quoted) : Boolean(textSplit.quoted);
    const shownHtml = showQuoted ? sanitized : htmlSplit.visible;
    const shownText = showQuoted ? textBody : textSplit.visible;

    // F06 fix: An email with body_html set but empty (or whitespace-only)
    // would render an empty iframe instead of the "No content" fallback.
    // Treat empty HTML the same as missing HTML so the fallback fires.
    const hasHtmlContent = isHtml && Boolean(shownHtml && shownHtml.trim());

    // Remote images are fetched server-side and handed to the iframe as blob
    // URLs. The CSP blocks `https:` images on purpose (exfiltration — see the
    // S12 note in `server.py`), so without this every picture in the mail is
    // a broken box: 252 of 300 sampled bodies on dev reference one. `cid:`
    // inline images are NOT resolved here — nothing stores inline attachments
    // to resolve them against yet.
    const displayHtml = useProxiedEmailImages(shownHtml, getAuthHeader, {
        commId: entry.comm_id,
        attachments: entry.attachments,
    });

    const dateStr = entry.comm_date || entry.sent_at || entry.date || entry.scheduled_date;
    const dateInfo = formatEmailDate(dateStr, { zone: displayZone });

    // F06 fix: Use shared useIframeAutoResize hook (includes polling for dynamic content)
    useIframeAutoResize(iframeRef, [isHtml, showQuoted, collapsed, displayHtml]);

    const ccList = entry.cc_emails || [];
    // "To:" is ENVELOPE truth and comes from `to_emails`, always.
    const toList = (entry.to_emails || []).filter(Boolean);
    const toDisplay = toList.join(', ') || entry.to || entry.contact_email || '';

    // BCC recipients surfaced only when they say something To/Cc lines do not.
    const addressed = new Set(
        [...toList, ...ccList, ...(entry.bcc_emails || [])].map((a) => (a || '').toLowerCase())
    );
    const deliveredElsewhere = (entry.recipients || [])
        .map((r) => r.mail)
        .filter((mail) => mail && !addressed.has(mail.toLowerCase()));

    const hasAttachment = Boolean(entry.has_attachments || (entry.attachments || []).length);

    // Only the parts a reader would think of as FILES. An inline image is
    // already rendered in the body — listing it again turns a signature with
    // a logo and two social icons into a message with three attachments,
    // which is what every mail client avoids by making the same distinction.
    const downloadable = (entry.attachments || []).filter(
        (a) => a?.attachment_id && !a.inline,
    );
    const preview = entry.snippet || textSplit.visible || entry.body || '';
    const actionable = canReplyAllowed && !entry.__synthetic;

    // The "sent as" chip and the synthetic badge have no slot in the design's
    // header, so they render under the recipients where an expanded message
    // has room — never in the collapsed row, which is one line by contract.
    const outboundFrom = entry.direction === 'outbound' ? senderAddress : '';
    const distroMismatch = Boolean(
        inbox?.distro_smtp
        && inbox.reply_settings?.enforce_distro_reply !== false
        && outboundFrom
        && outboundFrom !== inbox.distro_smtp
    );

    return (
        <div
            className={`border-b border-[#EDEBE6] ${collapsed ? 'bg-[#FDFCFB]' : 'bg-white'}`}
            data-testid={`thread-message-${entry.comm_id}`}
            // #715 — the anchor a reader scrolls to when the bell opened this
            // particular message (`location.state.focusCommId`).
            data-comm-id={entry.comm_id || undefined}
        >
            {/* The whole header toggles — see the note at the top of the file. */}
            <div
                role="button"
                tabIndex={0}
                aria-expanded={!collapsed}
                onClick={toggleCollapse}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleCollapse();
                    }
                }}
                data-testid={`thread-message-header-${entry.comm_id}`}
                className="flex items-start gap-[14px] px-7 py-[14px] cursor-pointer text-left w-full"
            >
                <span
                    aria-hidden="true"
                    style={{ background: avatarSolid(senderName || displayName) }}
                    className="flex-shrink-0 grid place-items-center w-8 h-8 rounded-full
                               text-[11.5px] font-semibold text-white"
                >
                    {initials(senderName || displayName)}
                </span>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className="flex-shrink-0 text-[13.5px] font-semibold text-[#0C0A09] whitespace-nowrap">
                            {displayName}
                        </span>
                        {senderAddress && senderAddress !== displayName && (
                            <span className="text-[12px] text-[#8A837E] truncate">
                                {senderAddress}
                            </span>
                        )}
                    </div>

                    {collapsed ? (
                        <div
                            className="mt-[3px] text-[12.5px] text-[#A29B95] truncate"
                            data-testid={`thread-message-preview-${entry.comm_id}`}
                        >
                            {preview}
                        </div>
                    ) : (
                        <div className="mt-[3px] text-[12px] text-[#A29B95] space-y-[2px]">
                            <div className="truncate">to {toDisplay || '—'}</div>
                            {ccList.length > 0 && (
                                <div className="truncate">cc {ccList.join(', ')}</div>
                            )}
                            {(entry.bcc_emails || []).length > 0 && (
                                <div className="truncate">bcc {entry.bcc_emails.join(', ')}</div>
                            )}
                            {deliveredElsewhere.length > 0 && (
                                <div
                                    className="truncate"
                                    data-testid={`delivered-to-${entry.comm_id}`}
                                >
                                    delivered to {deliveredElsewhere.join(', ')}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <MailTagChips
                    tags={entry.tags}
                    className="flex-shrink-0 mt-[2px] max-w-[40%]"
                    testId={`thread-message-tag-chips-${entry.comm_id}`}
                />

                {hasAttachment && (
                    <Paperclip
                        className="flex-shrink-0 w-[14px] h-[14px] mt-[3px] text-[#A29B95]"
                        aria-label="Has attachment"
                        data-testid={`thread-message-attachment-${entry.comm_id}`}
                    />
                )}

                <span
                    title={dateInfo?.full || ''}
                    className="flex-shrink-0 mt-[2px] text-[12px] text-[#8A837E] whitespace-nowrap"
                    data-testid={`thread-message-date-${entry.comm_id}`}
                >
                    {dateInfo?.stamp || ''}
                </span>

                {actionable && (
                    <>
                        <HeaderAction
                            icon={CornerUpLeft}
                            label="Reply in this thread"
                            onClick={() => onReply?.(entry)}
                            testId={`thread-header-reply-${entry.comm_id}`}
                        />
                        <HeaderAction
                            icon={CornerUpRight}
                            label="Forward this message"
                            onClick={() => onForward?.(entry)}
                            testId={`thread-header-forward-${entry.comm_id}`}
                        />
                    </>
                )}

                <div className="relative flex-shrink-0">
                    <HeaderAction
                        icon={MoreHorizontal}
                        label="More"
                        onClick={() => setMenuOpen((v) => !v)}
                        testId={`thread-header-menu-${entry.comm_id}`}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                    />
                    {tagsOpen && typeof onChangeTags === 'function' && (
                        /* Clicks and keys stay inside: the header row is the collapse
                           toggle, and it answers Enter/Space too — typing a tag name
                           must not fold the message. */
                        <div
                            className="absolute right-0 top-[32px] z-[34]"
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                        >
                            <MailTagPicker
                                tags={entry.tags || []}
                                heading="Tags on this message"
                                testId={`thread-message-tags-${entry.comm_id}`}
                                onClose={() => setTagsOpen(false)}
                                onChange={(delta) => onChangeTags(entry, delta)}
                            />
                        </div>
                    )}
                    {menuOpen && (
                        <MessageMenu
                            entry={entry}
                            displayName={displayName}
                            onReply={actionable ? onReply : undefined}
                            onReplyAll={actionable ? onReplyAll : undefined}
                            onForward={actionable ? onForward : undefined}
                            onDelete={onDelete}
                            onMarkUnread={onMarkUnread}
                            onPrint={() => window.print()}
                            onEditTags={typeof onChangeTags === 'function'
                                ? () => setTagsOpen(true)
                                : undefined}
                            onClose={() => setMenuOpen(false)}
                        />
                    )}
                </div>

                {collapsed
                    ? <ChevronDown className="flex-shrink-0 w-4 h-4 mt-[4px] text-[#A29B95]" aria-hidden="true" />
                    : <ChevronUp className="flex-shrink-0 w-4 h-4 mt-[4px] text-[#A29B95]" aria-hidden="true" />}
            </div>

            {!collapsed && (
                /* 74px = 28px gutter + 32px avatar + 14px gap, so the body hangs
                   off the sender's name rather than the page edge. */
                <div className="pt-0.5 pr-7 pb-5 pl-[74px] relative">
                    {(entry.__synthetic || outboundFrom) && (
                        <div className="flex items-center gap-2 mb-2.5">
                            {entry.__synthetic && <SyntheticMessageBadge />}
                            {outboundFrom && (
                                distroMismatch ? (
                                    <span
                                        className="inline-flex items-center gap-1 text-[10px] font-medium
                                                   text-amber-700 bg-amber-50 px-2 py-0.5 rounded"
                                        data-testid={`sent-as-personal-${entry.comm_id}`}
                                    >
                                        ⚠ sent as {outboundFrom}
                                    </span>
                                ) : (
                                    <span
                                        className="inline-flex items-center gap-1 text-[10px] font-medium
                                                   text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded"
                                        data-testid={`sent-as-distro-${entry.comm_id}`}
                                    >
                                        ✓ sent as {outboundFrom}
                                    </span>
                                )
                            )}
                        </div>
                    )}

                    <div className="max-w-[78ch] overflow-x-auto">
                        {hasHtmlContent && !loaded && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
                                <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                            </div>
                        )}
                        {hasHtmlContent ? (
                            <iframe
                                ref={iframeRef}
                                sandbox="allow-same-origin"
                                srcDoc={displayHtml}
                                className="w-full border-0 block"
                                style={{ minHeight: '64px' }}
                                title="Email content"
                                onLoad={() => setLoaded(true)}
                            />
                        ) : shownText ? (
                            <pre className="whitespace-pre-wrap font-sans break-words
                                            text-[14px] leading-[1.65] text-[#26221F]">
                                {shownText}
                            </pre>
                        ) : (
                            <p className="text-[14px] leading-[1.65] text-[#A29B95] italic">
                                No content
                            </p>
                        )}
                    </div>

                    {hasQuote && (
                        <button
                            type="button"
                            onClick={() => setShowQuoted((v) => !v)}
                            className="mt-2 px-2 py-0.5 rounded bg-[#F4F2EE] hover:bg-[#EDEBE6]
                                       text-[#8A837E] text-[11.5px] leading-none"
                            aria-expanded={showQuoted}
                            title={showQuoted ? 'Hide quoted text' : 'Show quoted text'}
                            data-quote-marker={htmlSplit.marker || 'text'}
                            data-testid={`quote-toggle-${entry.comm_id}`}
                        >
                            {showQuoted ? 'Hide quoted text' : '··· Show quoted text'}
                        </button>
                    )}

                    {downloadable.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {downloadable.map((att) => (
                                <button
                                    key={att.attachment_id}
                                    type="button"
                                    onClick={() => onDownloadAttachment?.(entry, att)}
                                    disabled={typeof onDownloadAttachment !== 'function'}
                                    title={`Download ${att.filename}`}
                                    className="inline-flex items-center gap-[9px] py-[9px] px-[13px]
                                               border border-[#E4E1DB] rounded-[10px] bg-[#FBFAF9]
                                               hover:bg-[#F4F2EE] disabled:opacity-60
                                               disabled:cursor-not-allowed text-left"
                                    data-testid={`thread-attachment-${att.attachment_id}`}
                                >
                                    <Paperclip className="w-[15px] h-[15px] text-[#57504B] flex-shrink-0" />
                                    <span className="min-w-0">
                                        <span className="block text-[12.5px] font-medium text-[#0C0A09] truncate">
                                            {att.filename || 'Attachment'}
                                        </span>
                                        <span className="block text-[11px] text-[#A29B95]">
                                            {formatBytes(att.size)}
                                        </span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {composer && <div ref={composerRef}>{composer}</div>}
        </div>
    );
});

export default ThreadMessage;
